import { z } from "zod";
import { PERMISSIONS, ROLES, hasPermission } from "../core";
import type { Store } from "../store";
import type { Alert } from "../types";
import { DEFAULT_SETTINGS, type Coupon, type Customer, type DiningTable, type MenuCategory, type MenuItem, type MenuSize, type Promotion, type User } from "../types";
import { adjustLoyalty } from "./orders";
import { sha256Hex, PIN_ITERATIONS, RECOVERY_ITERATIONS, audit, constantTimeEqual, DomainError, fail, flushAudits, getSettings, hashSecret, makeCtx, newId, parse, requirePermission, type Actor, type Ctx } from "./context";

const Id = z.string().min(1).max(128);
const Money = z.number().int().min(0).max(1_000_000_000);
const Bps = z.number().int().min(0).max(10_000);
const Minutes = z.number().int().min(0).max(1440);
const DateText = z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable().default(null);
const Login = z.string().trim().toLowerCase().min(3).max(254).regex(/^[a-z0-9._@+-]+$/, "Use letters, numbers, dots or an email address");
export const PasswordSchema = z.string().min(8, "Password must be at least 8 characters").max(128);
const PinSchema = z.string().regex(/^\d{4,8}$/, "PIN must be 4 to 8 digits");
const StaffFields = { displayName: z.string().trim().min(2).max(100), role: z.enum(ROLES), permissions: z.array(z.enum(PERMISSIONS)).max(PERMISSIONS.length).default([]) };
export const StaffCreateInputSchema = z.object({ uid: Id.optional(), email: Login, ...StaffFields, password: PasswordSchema.optional(), pin: PinSchema.optional(),
  /** Off when the Super Admin hands over a password they chose and does not want the forced change at first sign-in. */
  mustChangePassword: z.boolean().default(true)
});

async function setPassword(ctx: Ctx, uid: string, password: string): Promise<void> {
  const { hash, salt } = await hashSecret(password);
  ctx.tx.set("staffSecrets", { ...(await ctx.tx.get("staffSecrets", uid) ?? { id: uid }), passwordHash: hash, passwordSalt: salt });
}
async function setPin(ctx: Ctx, uid: string, pin: string): Promise<void> {
  const { hash, salt } = await hashSecret(pin, undefined, PIN_ITERATIONS);
  const secret = await ctx.tx.get("staffSecrets", uid) ?? { id: uid };
  delete secret.pinLockedUntil;
  ctx.tx.set("staffSecrets", { ...secret, pinHash: hash, pinSalt: salt, pinIterations: PIN_ITERATIONS, failedPinAttempts: 0 });
}
const activeSuperAdmins = async (ctx: Ctx) => (await ctx.tx.query("users", [{ field: "role", op: "==", value: "super_admin" }])).filter((user) => !user.disabled);

/** One-time creation of the first Super Admin. Refuses to run once any Super Admin exists. */
export async function bootstrap(ctx: Ctx, raw: unknown) {
  const input = parse(z.object({ email: Login, displayName: StaffFields.displayName, password: PasswordSchema, mustChangePassword: z.boolean().default(false) }), raw);
  if ((await ctx.tx.query("users", [{ field: "role", op: "==", value: "super_admin" }])).length > 0) fail("failed-precondition", "Setup has already been completed.");
  const id = newId();
  ctx.tx.set("users", { id, email: input.email, displayName: input.displayName, role: "super_admin", permissions: [], disabled: false, mustChangePassword: input.mustChangePassword, hasPin: false, createdAt: ctx.iso, createdBy: "bootstrap", schemaVersion: 1 });
  await setPassword(ctx, id, input.password);
  if (!await ctx.tx.get("settings", "restaurant")) ctx.tx.set("settings", { ...DEFAULT_SETTINGS, updatedAt: ctx.iso });
  audit({ ...ctx, actor: { id, name: input.displayName, role: "super_admin", permissions: [] } }, "super_admin.bootstrapped", "user", id);
  return { uid: id };
}

export async function createStaff(ctx: Ctx, raw: unknown) {
  requirePermission(ctx, "staff.manage");
  const input = parse(StaffCreateInputSchema, raw);
  if ((await ctx.tx.query("users", [{ field: "email", op: "==", value: input.email }])).length > 0) fail("failed-precondition", "A staff account with this login already exists.");
  const id = input.uid ?? newId();
  if (await ctx.tx.get("users", id)) fail("failed-precondition", "This account already exists.");
  ctx.tx.set("users", { id, email: input.email, displayName: input.displayName, role: input.role, permissions: input.permissions, disabled: false, mustChangePassword: input.mustChangePassword, hasPin: Boolean(input.pin), createdAt: ctx.iso, createdBy: ctx.actor.id, schemaVersion: 1 });
  if (input.password) await setPassword(ctx, id, input.password);
  if (input.pin) await setPin(ctx, id, input.pin);
  audit(ctx, "staff.created", "user", id, { login: input.email, role: input.role, permissions: input.permissions });
  return { uid: id };
}

export async function updateStaff(ctx: Ctx, raw: unknown) {
  requirePermission(ctx, "staff.manage");
  const input = parse(z.object({ uid: Id, ...StaffFields, disabled: z.boolean() }), raw);
  const user = await ctx.tx.get("users", input.uid) ?? fail("not-found", "Staff account not found.");
  if (user.id === ctx.actor.id && (input.role !== user.role || input.disabled)) fail("failed-precondition", "You cannot change your own role or disable yourself.");
  if (user.role === "super_admin" && !user.disabled && (input.role !== "super_admin" || input.disabled) && (await activeSuperAdmins(ctx)).length <= 1) fail("failed-precondition", "At least one active Super Admin must remain.");
  ctx.tx.set("users", { ...user, displayName: input.displayName, role: input.role, permissions: input.permissions, disabled: input.disabled, updatedAt: ctx.iso });
  const action = input.disabled !== user.disabled ? (input.disabled ? "staff.disabled" : "staff.enabled") : "staff.access_changed";
  audit(ctx, action, "user", user.id, { before: { role: user.role, permissions: user.permissions, disabled: user.disabled }, after: { role: input.role, permissions: input.permissions, disabled: input.disabled } });
  return { uid: user.id };
}

export async function resetStaffPassword(ctx: Ctx, raw: unknown) {
  requirePermission(ctx, "staff.manage");
  const input = parse(z.object({ uid: Id, password: PasswordSchema.optional() }), raw);
  const user = await ctx.tx.get("users", input.uid) ?? fail("not-found", "Staff account not found.");
  if (input.password) await setPassword(ctx, user.id, input.password);
  ctx.tx.set("users", { ...user, mustChangePassword: true, updatedAt: ctx.iso });
  audit(ctx, "staff.password_reset", "user", user.id);
  return { uid: user.id };
}

export async function setStaffPin(ctx: Ctx, raw: unknown) {
  const input = parse(z.object({ uid: Id.optional(), pin: PinSchema }), raw);
  const uid = input.uid ?? ctx.actor.id;
  if (uid !== ctx.actor.id) requirePermission(ctx, "staff.manage");
  const user = await ctx.tx.get("users", uid) ?? fail("not-found", "Staff account not found.");
  await setPin(ctx, uid, input.pin);
  ctx.tx.set("users", { ...user, hasPin: true, updatedAt: ctx.iso });
  audit(ctx, "staff.pin_set", "user", uid);
  return { uid };
}

/** Local mode only: the signed-in user proves the current password and chooses a new one. */
export async function changeOwnPassword(ctx: Ctx, raw: unknown) {
  const input = parse(z.object({ currentPassword: z.string().min(1).max(128), newPassword: PasswordSchema }), raw);
  const user = await ctx.tx.get("users", ctx.actor.id) ?? fail("unauthenticated", "Sign in again.");
  const secret = await ctx.tx.get("staffSecrets", user.id);
  if (!secret?.passwordHash || !secret.passwordSalt || !constantTimeEqual((await hashSecret(input.currentPassword, secret.passwordSalt)).hash, secret.passwordHash)) fail("permission-denied", "Current password is incorrect.");
  if (input.currentPassword === input.newPassword) fail("invalid-argument", "Choose a different password.");
  await setPassword(ctx, user.id, input.newPassword);
  ctx.tx.set("users", { ...user, mustChangePassword: false, updatedAt: ctx.iso });
  audit(ctx, "staff.password_changed", "user", user.id);
  return { ok: true };
}

/** Firebase mode: called after Firebase Auth accepted the new password, to lift the first-login lock. */
export async function markPasswordChanged(ctx: Ctx) {
  const user = await ctx.tx.get("users", ctx.actor.id) ?? fail("unauthenticated", "Sign in again.");
  ctx.tx.set("users", { ...user, mustChangePassword: false, updatedAt: ctx.iso });
  audit(ctx, "staff.password_changed", "user", user.id);
  return { ok: true };
}

const failedLogins = new Map<string, { count: number; until: number }>();
/** Local-mode sign-in against the PBKDF2 hash, with a short lockout after repeated failures. */
export async function localLogin(store: Store, login: string, password: string, now = new Date()): Promise<User> {
  const email = login.trim().toLowerCase();
  const state = failedLogins.get(email);
  if (state && state.until > now.getTime()) throw new DomainError("permission-denied", "Too many attempts. Wait a minute and try again.");
  const user = await store.transaction(async (tx) => {
    const found = (await tx.query("users", [{ field: "email", op: "==", value: email }]))[0];
    const secret = found ? await tx.get("staffSecrets", found.id) : undefined;
    // Hash even when the account is unknown so response time does not reveal which logins exist.
    const { hash } = await hashSecret(password, secret?.passwordSalt ?? "AAAAAAAAAAAAAAAAAAAAAA==");
    const valid = Boolean(found && !found.disabled && secret?.passwordHash && constantTimeEqual(hash, secret.passwordHash));
    const actor: Actor = found ? { id: found.id, name: found.displayName, role: found.role, permissions: found.permissions } : { id: "anonymous", name: email.slice(0, 60), role: "kitchen", permissions: [] };
    const ctx = makeCtx(tx, actor, now);
    audit(ctx, valid ? "auth.login" : "auth.login_failed", "user", found?.id ?? "unknown");
    await flushAudits(ctx);
    return valid ? found : undefined;
  });
  if (!user) {
    const count = (state?.count ?? 0) + 1;
    failedLogins.set(email, { count: count >= 5 ? 0 : count, until: count >= 5 ? now.getTime() + 60_000 : 0 });
    throw new DomainError("permission-denied", "Incorrect login or password.");
  }
  failedLogins.delete(email);
  return user;
}

const RECOVERY_ALPHABET = "23456789ABCDEFGHJKLMNPQRSTUVWXYZ";
/**
 * Gives a Super Admin a one-time recovery code for a forgotten password. Only its hash is stored;
 * the code is returned once, to be printed and locked away. Creating a new code replaces the old one.
 */
export async function createRecoveryCode(ctx: Ctx) {
  if (ctx.actor.role !== "super_admin") fail("permission-denied", "Only a Super Admin can create a recovery code.");
  const user = await ctx.tx.get("users", ctx.actor.id) ?? fail("unauthenticated", "Sign in again.");
  const raw = Array.from(crypto.getRandomValues(new Uint8Array(20)), (byte) => RECOVERY_ALPHABET[byte % RECOVERY_ALPHABET.length]).join("");
  const { hash, salt } = await hashSecret(raw, undefined, RECOVERY_ITERATIONS);
  ctx.tx.set("staffSecrets", { ...(await ctx.tx.get("staffSecrets", user.id) ?? { id: user.id }), recoveryHash: hash, recoverySalt: salt, recoveryIterations: RECOVERY_ITERATIONS });
  ctx.tx.set("users", { ...user, hasRecoveryCode: true, updatedAt: ctx.iso });
  audit(ctx, "auth.recovery_code_created", "user", user.id);
  return { code: raw.match(/.{5}/g)!.join("-") };
}

/** Local mode: a Super Admin who forgot the password signs back in with the recovery code. The code works once. */
export async function recoverWithCode(store: Store, login: string, code: string, newPassword: string, now = new Date()): Promise<User> {
  const email = login.trim().toLowerCase(), key = `recover:${email}`;
  const check = PasswordSchema.safeParse(newPassword);
  if (!check.success) throw new DomainError("invalid-argument", check.error.issues[0]!.message);
  const state = failedLogins.get(key);
  if (state && state.until > now.getTime()) throw new DomainError("permission-denied", "Too many attempts. Wait five minutes and try again.");
  const user = await store.transaction(async (tx) => {
    const found = (await tx.query("users", [{ field: "email", op: "==", value: email }]))[0];
    const secret = found ? await tx.get("staffSecrets", found.id) : undefined;
    const { hash } = await hashSecret(code.toUpperCase().replace(/[^0-9A-Z]/g, ""), secret?.recoverySalt ?? "AAAAAAAAAAAAAAAAAAAAAA==", secret?.recoveryIterations ?? 150_000);
    const valid = Boolean(found && !found.disabled && found.role === "super_admin" && secret?.recoveryHash && constantTimeEqual(hash, secret.recoveryHash));
    const ctx = makeCtx(tx, found ? { id: found.id, name: found.displayName, role: found.role, permissions: found.permissions } : { id: "anonymous", name: email.slice(0, 60), role: "kitchen", permissions: [] }, now);
    let recovered: User | undefined;
    if (valid && found && secret) {
      await setPassword(ctx, found.id, newPassword);
      const updated = (await tx.get("staffSecrets", found.id))!;
      delete updated.recoveryHash; delete updated.recoverySalt; delete updated.recoveryIterations;
      tx.set("staffSecrets", updated);
      recovered = { ...found, hasRecoveryCode: false, mustChangePassword: false, updatedAt: ctx.iso };
      tx.set("users", recovered);
    }
    audit(ctx, valid ? "auth.recovered_with_code" : "auth.recovery_failed", "user", found?.id ?? "unknown");
    await flushAudits(ctx);
    return recovered;
  });
  if (!user) {
    const count = (state?.count ?? 0) + 1;
    failedLogins.set(key, { count: count >= 5 ? 0 : count, until: count >= 5 ? now.getTime() + 5 * 60_000 : 0 });
    throw new DomainError("permission-denied", "The login or recovery code is not correct.");
  }
  failedLogins.delete(key);
  return user;
}

/**
 * Lets a manager's phone fetch alerts in the background. The token is returned once and only its
 * hash is kept; it can read alert texts and nothing else, and stops working when the account is disabled.
 */
export async function registerDevice(ctx: Ctx, raw: unknown) {
  requirePermission(ctx, "reports.read");
  const input = parse(z.object({ label: z.string().trim().max(80).default("Phone") }), raw);
  const id = newId(), token = Array.from(crypto.getRandomValues(new Uint8Array(32)), (byte) => byte.toString(16).padStart(2, "0")).join("");
  ctx.tx.set("notificationDevices", { id, userId: ctx.actor.id, userName: ctx.actor.name, label: input.label, tokenHash: await sha256Hex(token), revoked: false, createdAt: ctx.iso, schemaVersion: 1 });
  audit(ctx, "device.registered", "device", id, { label: input.label });
  return { device: `${id}.${token}`, since: ctx.iso };
}

/** Signing out of the phone app stops its alerts at once: only someone signed in gets the management notifications. */
export async function revokeDevice(ctx: Ctx, raw: unknown) {
  const input = parse(z.object({ id: Id }), raw);
  const device = await ctx.tx.get("notificationDevices", input.id);
  if (!device || device.revoked) return { revoked: false };
  if (device.userId !== ctx.actor.id) fail("permission-denied", "This phone is linked to another account.");
  ctx.tx.set("notificationDevices", { ...device, revoked: true });
  audit(ctx, "device.revoked", "device", device.id, { label: device.label });
  return { revoked: true };
}

/** Alerts raised after `after`, oldest first, for a registered phone. */
export async function deviceAlerts(store: Store, credential: string, after: string): Promise<Alert[]> {
  const [, id, token] = /^([0-9a-f-]{36})\.([0-9a-f]{64})$/.exec(credential) ?? [];
  if (!id || !token || Number.isNaN(Date.parse(after))) throw new DomainError("unauthenticated", "Unknown device.");
  const hash = await sha256Hex(token);
  return store.transaction(async (tx) => {
    const device = await tx.get("notificationDevices", id);
    const user = device && !device.revoked && constantTimeEqual(device.tokenHash, hash) ? await tx.get("users", device.userId) : undefined;
    if (!user || user.disabled || !hasPermission(user.role, "reports.read", user.permissions)) throw new DomainError("unauthenticated", "Unknown device.");
    const alerts = await tx.query("alerts", [{ field: "createdAt", op: ">=", value: after }]);
    return alerts.filter((alert) => alert.createdAt > after).sort((a, b) => a.createdAt.localeCompare(b.createdAt)).slice(0, 20);
  });
}

export async function updateSettings(ctx: Ctx, raw: unknown) {
  requirePermission(ctx, "settings.manage");
  const Threshold = z.object({ maxDiscountBps: Bps, maxRefundMinor: z.number().int().min(0).max(Number.MAX_SAFE_INTEGER) });
  const input = parse(z.object({
    name: z.string().trim().min(2).max(80), addressLines: z.array(z.string().max(80)).max(4), phone: z.string().max(40), receiptFooter: z.string().max(200),
    taxBps: Bps, taxLabel: z.string().min(1).max(20), serviceChargeBps: Bps, serviceChargeDineInOnly: z.boolean(), kitchenDisplayEnabled: z.boolean().default(false), autoLockMinutes: z.number().int().min(0).max(240).default(15), loyaltyEnabled: z.boolean(),
    loyaltySpendPerPointMinor: z.number().int().min(100).max(100_000_000), loyaltyPointValueMinor: Money, alertDiscountBps: Bps.default(2_000), stockBlocksSale: z.boolean().default(true),
    thresholds: z.object({ super_admin: Threshold, manager: Threshold, viewer: Threshold.default({ maxDiscountBps: 0, maxRefundMinor: 0 }), accountant: Threshold.default({ maxDiscountBps: 0, maxRefundMinor: 0 }), cashier: Threshold, waiter: Threshold, kitchen: Threshold }),
    publicStatusBaseUrl: z.union([z.literal(""), z.string().url().startsWith("https://").max(200)])
  }), raw);
  const before = await getSettings(ctx.tx);
  ctx.tx.set("settings", { ...before, ...input, id: "restaurant", updatedAt: ctx.iso });
  audit(ctx, "settings.updated", "settings", "restaurant", { before: { taxBps: before.taxBps, serviceChargeBps: before.serviceChargeBps, thresholds: before.thresholds }, after: { taxBps: input.taxBps, serviceChargeBps: input.serviceChargeBps, thresholds: input.thresholds } });
  return { ok: true };
}

/** Leaves an audit trail when the backup password is set or replaced. The password itself never reaches the engine. */
export async function recordBackupPassword(ctx: Ctx) {
  requirePermission(ctx, "data.backup");
  audit(ctx, "backup.password_set", "settings", "backup");
  return { ok: true };
}

export async function saveWaiters(ctx: Ctx, raw: unknown) {
  requirePermission(ctx, "tables.manage");
  const input = parse(z.object({ names: z.array(z.string().trim().min(1).max(60)).max(100) }), raw);
  const waiterNames = [...new Set(input.names)].sort((a, b) => a.localeCompare(b));
  ctx.tx.set("settings", { ...await getSettings(ctx.tx), waiterNames, updatedAt: ctx.iso });
  audit(ctx, "waiters.updated", "settings", "restaurant", { waiterNames });
  return { waiterNames };
}

export async function saveCategory(ctx: Ctx, raw: unknown) {
  requirePermission(ctx, "menu.manage");
  const input = parse(z.object({ id: Id.optional(), name: z.string().trim().min(1).max(60), sortOrder: z.number().int().min(0).max(9999).default(0), active: z.boolean().default(true) }), raw);
  const existing = input.id ? await ctx.tx.get("menuCategories", input.id) ?? fail("not-found", "Category not found.") : undefined;
  const category: MenuCategory = { ...existing, id: existing?.id ?? newId(), name: input.name, sortOrder: input.sortOrder, active: input.active, schemaVersion: 1 };
  ctx.tx.set("menuCategories", category);
  audit(ctx, existing ? "menu.category_updated" : "menu.category_created", "menuCategory", category.id, { name: category.name, active: category.active });
  return { id: category.id };
}

const RecipeSchema = z.array(z.object({ inventoryItemId: Id, qtyMilli: z.number().int().positive().max(100_000_000) })).max(40);
const Option = z.object({ id: Id.optional(), name: z.string().trim().min(1).max(60), priceMinor: Money, recipe: RecipeSchema.optional(), costMinor: Money.optional() });
export async function saveMenuItem(ctx: Ctx, raw: unknown) {
  requirePermission(ctx, "menu.manage");
  const input = parse(z.object({
    id: Id.optional(), categoryId: Id, name: z.string().trim().min(1).max(80), description: z.string().max(300).default(""), priceMinor: Money, costMinor: Money.default(0),
    imageUrl: z.string().max(400_000).default(""), barcode: z.string().max(64).default(""), active: z.boolean().default(true), sortOrder: z.number().int().min(0).max(9999).default(0),
    sizes: z.array(Option).max(8).default([]),
    modifierGroups: z.array(z.object({ id: Id.optional(), name: z.string().trim().min(1).max(60), min: z.number().int().min(0).max(10), max: z.number().int().min(1).max(10), options: z.array(Option).min(1).max(20) })).max(8).default([]),
    recipe: RecipeSchema.default([]),
    availability: z.object({ days: z.array(z.number().int().min(0).max(6)).max(7), fromMinutes: Minutes, toMinutes: Minutes }).nullable().default(null)
  }), raw);
  if (!await ctx.tx.get("menuCategories", input.categoryId)) fail("not-found", "Category not found.");
  for (const group of input.modifierGroups) if (group.min > group.max || group.max > group.options.length) fail("invalid-argument", `Check the option limits for ${group.name}.`);
  const existing = input.id ? await ctx.tx.get("menuItems", input.id) ?? fail("not-found", "Menu item not found.") : undefined;
  // A size or add-on sent without a recipe (from a POS not yet updated) keeps the one it had; with none at all, a size
  // falls back to the dish's own recipe.
  const previous = new Map([...existing?.sizes ?? [], ...existing?.modifierGroups.flatMap((group) => group.options) ?? []].map((entry) => [entry.id, entry.recipe]));
  const withIds = <T extends { id?: string | undefined; name: string; priceMinor: number; recipe?: MenuSize["recipe"] | undefined; costMinor?: number | undefined }>(entries: T[]) =>
    entries.map(({ recipe, costMinor, ...entry }) => {
      const kept = recipe ?? (entry.id ? previous.get(entry.id) : undefined);
      return { ...entry, id: entry.id ?? newId(), ...(kept ? { recipe: kept } : {}), ...(costMinor !== undefined ? { costMinor } : {}) };
    });
  const item: MenuItem = {
    ...existing, ...input, id: existing?.id ?? newId(), sizes: withIds(input.sizes),
    modifierGroups: input.modifierGroups.map((group) => ({ ...group, id: group.id ?? newId(), options: withIds(group.options) })),
    createdAt: existing?.createdAt ?? ctx.iso, updatedAt: ctx.iso, schemaVersion: 1
  };
  ctx.tx.set("menuItems", item);
  audit(ctx, existing ? "menu.item_updated" : "menu.item_created", "menuItem", item.id, { name: item.name, priceMinor: item.priceMinor, ...(existing && existing.priceMinor !== item.priceMinor ? { previousPriceMinor: existing.priceMinor } : {}), active: item.active });
  return { id: item.id };
}

export async function saveTable(ctx: Ctx, raw: unknown) {
  requirePermission(ctx, "tables.manage");
  const input = parse(z.object({ id: Id.optional(), name: z.string().trim().min(1).max(30), area: z.string().max(40).default("Main"), seats: z.number().int().min(1).max(50).default(4), active: z.boolean().default(true), sortOrder: z.number().int().min(0).max(9999).default(0) }), raw);
  const existing = input.id ? await ctx.tx.get("tables", input.id) ?? fail("not-found", "Table not found.") : undefined;
  if (existing?.activeOrderId && !input.active) fail("failed-precondition", "Settle the open bill before removing this table from service.");
  const table: DiningTable = { status: "available", activeOrderId: null, waiterId: null, waiterName: null, reservedFor: null, ...existing, ...input, id: existing?.id ?? newId(), schemaVersion: 1 };
  ctx.tx.set("tables", table);
  audit(ctx, existing ? "table.updated" : "table.created", "table", table.id, { name: table.name, active: table.active });
  return { id: table.id };
}

export async function reserveTable(ctx: Ctx, raw: unknown) {
  requirePermission(ctx, "orders.create");
  const input = parse(z.object({ tableId: Id, reservedFor: z.string().trim().max(80).nullable() }), raw);
  const table = await ctx.tx.get("tables", input.tableId) ?? fail("not-found", "Table not found.");
  if (table.activeOrderId) fail("failed-precondition", "This table has an open bill.");
  ctx.tx.set("tables", { ...table, status: input.reservedFor ? "reserved" : "available", reservedFor: input.reservedFor || null });
  audit(ctx, input.reservedFor ? "table.reserved" : "table.released", "table", table.id, { name: table.name });
  return { id: table.id };
}

export async function saveCustomer(ctx: Ctx, raw: unknown) {
  requirePermission(ctx, "customers.manage");
  const input = parse(z.object({
    id: Id.optional(), phone: z.string().regex(/^\+?[0-9]{9,15}$/, "Enter a valid phone number"), name: z.string().trim().max(100).default(""),
    birthday: z.union([z.literal(""), z.string().regex(/^\d{4}-\d{2}-\d{2}$/)]).default(""), notes: z.string().max(500).default(""),
    marketingConsent: z.boolean().default(false), active: z.boolean().default(true)
  }), raw);
  const existing = input.id ? await ctx.tx.get("customers", input.id) ?? fail("not-found", "Customer not found.") : undefined;
  const samePhone = (await ctx.tx.query("customers", [{ field: "phone", op: "==", value: input.phone }])).find((entry) => entry.id !== existing?.id);
  if (samePhone) fail("failed-precondition", "Another customer already uses this phone number.");
  const customer: Customer = { points: 0, totalSpentMinor: 0, orderCount: 0, lastOrderAt: null, createdAt: ctx.iso, ...existing, ...input, id: existing?.id ?? newId(), updatedAt: ctx.iso, schemaVersion: 1 };
  ctx.tx.set("customers", customer);
  audit(ctx, existing ? "customer.updated" : "customer.created", "customer", customer.id, { marketingConsent: customer.marketingConsent });
  return { id: customer.id };
}

export async function adjustCustomerPoints(ctx: Ctx, raw: unknown) {
  requirePermission(ctx, "promotions.manage");
  const input = parse(z.object({ idempotencyKey: z.string().uuid(), customerId: Id, points: z.number().int().min(-1_000_000).max(1_000_000).refine((value) => value !== 0, "Enter a non-zero amount"), reason: z.string().min(3).max(200) }), raw);
  if (!await ctx.tx.get("customers", input.customerId)) fail("not-found", "Customer not found.");
  await adjustLoyalty(ctx, input.customerId, input.points, "adjust", null, input.reason);
  audit(ctx, "loyalty.adjusted", "customer", input.customerId, { points: input.points }, { reason: input.reason });
  return { ok: true };
}

export async function savePromotion(ctx: Ctx, raw: unknown) {
  requirePermission(ctx, "promotions.manage");
  const input = parse(z.object({
    id: Id.optional(), name: z.string().trim().min(2).max(80), kind: z.enum(["percentage", "fixed", "happy_hour", "bogo", "combo", "manager_special", "staff", "birthday"]),
    scope: z.enum(["bill", "item"]).default("bill"), valueBps: Bps.default(0), valueMinor: Money.default(0), itemIds: z.array(Id).max(100).default([]), categoryIds: z.array(Id).max(50).default([]),
    minSpendMinor: Money.default(0), maxDiscountMinor: Money.default(0), startDate: DateText, endDate: DateText, days: z.array(z.number().int().min(0).max(6)).max(7).default([]),
    fromMinutes: Minutes.default(0), toMinutes: Minutes.default(0), buyQty: z.number().int().min(0).max(20).default(1), getQty: z.number().int().min(0).max(20).default(1),
    autoApply: z.boolean().default(false), stackable: z.boolean().default(true), active: z.boolean().default(true)
  }), raw);
  if (input.kind === "bogo" && (input.buyQty < 1 || input.getQty < 1)) fail("invalid-argument", "Buy and get quantities must be at least 1.");
  if (input.kind === "combo" && input.itemIds.length < 2) fail("invalid-argument", "A combo needs at least two items.");
  if (!["bogo", "combo"].includes(input.kind) && input.valueBps === 0 && input.valueMinor === 0) fail("invalid-argument", "Enter a discount value.");
  if ((input.kind === "staff" || input.kind === "manager_special") && input.autoApply) fail("invalid-argument", "Staff and manager discounts cannot apply automatically.");
  const existing = input.id ? await ctx.tx.get("promotions", input.id) ?? fail("not-found", "Promotion not found.") : undefined;
  const promotion: Promotion = { ...existing, ...input, scope: input.kind === "bogo" || input.kind === "combo" ? "item" : input.scope, id: existing?.id ?? newId(), createdAt: existing?.createdAt ?? ctx.iso, updatedAt: ctx.iso, schemaVersion: 1 };
  ctx.tx.set("promotions", promotion);
  audit(ctx, existing ? "promotion.updated" : "promotion.created", "promotion", promotion.id, { name: promotion.name, kind: promotion.kind, active: promotion.active });
  return { id: promotion.id };
}

export async function saveCoupon(ctx: Ctx, raw: unknown) {
  requirePermission(ctx, "promotions.manage");
  const input = parse(z.object({
    id: Id.optional(), code: z.string().trim().toUpperCase().regex(/^[A-Z0-9-]{3,24}$/, "Use 3–24 letters, numbers or dashes"), description: z.string().max(200).default(""),
    mode: z.enum(["percent", "fixed"]), value: z.number().int().positive().max(1_000_000_000), minSpendMinor: Money.default(0), maxDiscountMinor: Money.default(0),
    maxRedemptions: z.number().int().min(0).max(1_000_000).default(0), perCustomerLimit: z.number().int().min(0).max(1000).default(0),
    startDate: DateText, endDate: DateText, active: z.boolean().default(true)
  }), raw);
  if (input.mode === "percent" && input.value > 10_000) fail("invalid-argument", "Percentage cannot exceed 100%.");
  const existing = input.id ? await ctx.tx.get("coupons", input.id) ?? fail("not-found", "Coupon not found.") : undefined;
  const duplicate = (await ctx.tx.query("coupons", [{ field: "code", op: "==", value: input.code }])).find((entry) => entry.id !== existing?.id);
  if (duplicate) fail("failed-precondition", "This coupon code already exists.");
  const coupon: Coupon = { redeemedCount: 0, ...existing, ...input, id: existing?.id ?? newId(), createdAt: existing?.createdAt ?? ctx.iso, updatedAt: ctx.iso, schemaVersion: 1 };
  ctx.tx.set("coupons", coupon);
  audit(ctx, existing ? "coupon.updated" : "coupon.created", "coupon", coupon.id, { code: coupon.code, active: coupon.active });
  return { id: coupon.id };
}
